"use client";

import { Globe, Check, ChevronDown } from "lucide-react";
import { useTranslation } from "@/lib/i18n/context";

/* Endonyms — language-neutral, so they read the same in both UI locales.
   Illustrative of the 100+ content languages the AI can speak. */
const LANGS = [
  { name: "English", selected: false },
  { name: "Español", selected: true },
  { name: "Deutsch", selected: false },
  { name: "中文", selected: false },
  { name: "Português", selected: false },
  { name: "日本語", selected: false },
];

export function LanguageSection() {
  const { t } = useTranslation();
  const l = t.landing.cover.languageSection;

  return (
    <section className="relative max-w-[1320px] mx-auto px-5 md:px-12 py-20 md:py-[120px] z-[2]">
      <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-20 items-center">
        {/* Copy */}
        <div>
          <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {l.kicker}
          </div>
          <h2
            className="font-display text-[clamp(32px,3.8vw,52px)] leading-[1.06] tracking-[-0.02em] text-balance"
            style={{ fontWeight: 380 }}
          >
            {l.h2a} <em className="text-[var(--terracotta)] italic">{l.h2em}</em>
          </h2>
          <p className="mt-6 text-[16px] md:text-[18px] leading-[1.65] text-[var(--ink-soft)] max-w-[560px] text-pretty">
            {l.description}
          </p>
          <p className="mt-5 text-[16px] md:text-[18px] leading-[1.65] text-[var(--ink-soft)] max-w-[560px] text-pretty">
            {l.settingsNote}
          </p>
        </div>

        {/* Minimalist Settings → Content language dropdown mock */}
        <div className="w-full max-w-[440px] mx-auto lg:mx-0 lg:ml-auto">
          <div
            className="rounded-[18px] border overflow-hidden"
            style={{
              background: "var(--paper)",
              borderColor: "var(--hair)",
              boxShadow: "0 1px 2px rgba(26,24,21,0.04), 0 24px 60px rgba(26,24,21,0.08)",
            }}
          >
            {/* Breadcrumb chrome */}
            <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: "var(--hair)" }}>
              <Globe className="h-3.5 w-3.5 text-[var(--ink-soft)]" aria-hidden />
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
                {l.mockBreadcrumb} · {l.mockLabel}
              </span>
            </div>

            {/* Trigger row */}
            <div className="px-4 pt-4">
              <div
                className="flex items-center justify-between gap-3 rounded-xl border px-3.5 py-3"
                style={{ borderColor: "var(--terracotta)", background: "color-mix(in srgb, var(--terracotta) 6%, transparent)" }}
              >
                <span className="flex items-center gap-2.5">
                  <Globe className="h-4 w-4 text-[var(--terracotta)]" aria-hidden />
                  <span className="text-[14px] text-[var(--ink)]">Español</span>
                </span>
                <ChevronDown className="h-4 w-4 text-[var(--ink-soft)]" aria-hidden />
              </div>
            </div>

            {/* Open option list */}
            <div className="px-4 pb-4 pt-2">
              <ul className="rounded-xl border border-[var(--hair)] divide-y divide-[var(--hair)] overflow-hidden">
                {LANGS.map((lang) => (
                  <li
                    key={lang.name}
                    className="flex items-center justify-between gap-3 px-3.5 py-2.5"
                    style={{
                      background: lang.selected ? "color-mix(in srgb, var(--terracotta) 10%, transparent)" : "transparent",
                    }}
                  >
                    <span className={`text-[14px] ${lang.selected ? "text-[var(--terracotta)] font-medium" : "text-[var(--ink)]"}`}>
                      {lang.name}
                    </span>
                    {lang.selected && <Check className="h-4 w-4 text-[var(--terracotta)]" aria-hidden />}
                  </li>
                ))}
                <li className="px-3.5 py-2.5" style={{ background: "var(--paper-2)" }}>
                  <span className="font-mono text-[11px] uppercase tracking-[0.16em] font-medium text-[var(--terracotta)]">
                    + {l.mockHint}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
